import { useState } from 'react'
import { Button, Eyebrow, Field, Icon, Modal } from '@/ui'
import { cx } from '@/ui/cx'
import { RULE_ICONS, S } from './strings'
import styles from './CondoView.module.css'

export interface RuleDraft {
  id?: number
  icon: string
  title: string
  description: string
}

interface RuleModalProps {
  initial: RuleDraft
  pending: boolean
  error: boolean
  onClose: () => void
  onSave: (draft: RuleDraft) => void
}

export function RuleModal({ initial, pending, error, onClose, onSave }: RuleModalProps) {
  const [d, setD] = useState(initial)
  const editing = d.id !== undefined
  const valid = d.title.trim() !== '' && d.description.trim() !== ''
  return (
    <Modal
      title={editing ? S.ruleEditTitle : S.ruleAddTitle}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {S.cancel}
          </Button>
          <Button
            icon="check"
            disabled={!valid}
            loading={pending}
            onClick={() =>
              onSave({ ...d, title: d.title.trim(), description: d.description.trim() })
            }
          >
            {editing ? S.ruleSave : S.addRule}
          </Button>
        </>
      }
    >
      <Field
        label={S.ruleTitle}
        value={d.title}
        onChange={(title) => setD((s) => ({ ...s, title }))}
        placeholder={S.ruleTitlePlaceholder}
        autoFocus
      />
      <Field
        label={S.ruleDescription}
        value={d.description}
        onChange={(description) => setD((s) => ({ ...s, description }))}
        placeholder={S.ruleDescriptionPlaceholder}
        hint={S.ruleDescriptionHint}
        multiline
      />
      <div>
        <Eyebrow>{S.icon}</Eyebrow>
        <div className={styles.iconPicker} role="radiogroup" aria-label={S.icon}>
          {RULE_ICONS.map(({ name, label }) => {
            const on = name === d.icon
            return (
              <button
                key={name}
                type="button"
                role="radio"
                aria-checked={on}
                aria-label={label}
                title={label}
                className={cx(styles.iconTile, on && styles.iconTileOn)}
                onClick={() => setD((s) => ({ ...s, icon: name }))}
              >
                <Icon name={name} size={20} />
              </button>
            )
          })}
        </div>
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {S.genericError}
        </p>
      )}
    </Modal>
  )
}
